import type { FirstJobPlan, LocalShift, WageTerm } from '@wolhasudang/shared';
import { termFor } from '@wolhasudang/engine';

export function firstJobReceiptBasis(shift: LocalShift, wageTerms: WageTerm[]) {
  const term = shift.workplaceId ? termFor(wageTerms, shift.workplaceId, shift.date) : undefined;
  const breakMinutes = shift.actualBreakUnconfirmed ? undefined : shift.actualBreakMinutes;
  if (breakMinutes === undefined || !shift.actualStart || !shift.actualEnd) return { term, breakMinutes, workedMinutes: undefined, estimate: undefined };
  const workedMinutes = firstJobWorkedMinutes(shift.actualStart, shift.actualEnd, breakMinutes);
  return { term, breakMinutes, workedMinutes, estimate: term ? firstJobBaseEstimate(term.ordinaryHourlyRate, workedMinutes) : undefined };
}

export const firstJobChecklistLabels: Record<keyof FirstJobPlan['contractChecklist'], string> = {
  contractCopy: '서면 근로계약서 사본 받기',
  payTerms: '시급·임금 계산법 확인하기',
  scheduleAndBreak: '근무일·시간·휴게 확인하기',
  paydayAndMethod: '급여일·지급 방법 확인하기',
  guardianDocuments: '보호자 동의서·연령 증명 준비하기',
};

function clockMinutes(value: string): number {
  const [hour, minute] = value.split(':').map(Number);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour! < 0 || hour! > 23 || minute! < 0 || minute! > 59) throw new Error('INVALID_TIME');
  return hour! * 60 + minute!;
}

export function firstJobWorkedMinutes(start: string, end: string, breakMinutes = 0): number {
  const startMinutes = clockMinutes(start);
  let endMinutes = clockMinutes(end);
  if (endMinutes <= startMinutes) endMinutes += 24 * 60;
  const duration = endMinutes - startMinutes;
  if (!Number.isInteger(breakMinutes) || breakMinutes < 0 || breakMinutes >= duration || duration > 16 * 60) throw new Error('INVALID_SHIFT');
  return duration - breakMinutes;
}

export function firstJobBaseEstimate(hourlyRate: number, workedMinutes: number): number {
  if (!Number.isInteger(hourlyRate) || hourlyRate <= 0 || !Number.isInteger(workedMinutes) || workedMinutes <= 0) throw new Error('INVALID_ESTIMATE_INPUT');
  return Math.round(hourlyRate * workedMinutes / 60);
}

export function firstJobReceiptText(input: { workplaceName: string; shift: LocalShift; breakMinutes?: number | undefined; hourlyRate?: number | undefined }): string {
  if (!input.shift.actualStart || !input.shift.actualEnd) throw new Error('SHIFT_TIME_REQUIRED');
  const workedMinutes = input.breakMinutes === undefined ? undefined : firstJobWorkedMinutes(input.shift.actualStart, input.shift.actualEnd, input.breakMinutes);
  const estimate = input.hourlyRate && workedMinutes !== undefined ? firstJobBaseEstimate(input.hourlyRate, workedMinutes) : undefined;
  return [
    '월화수당 · 퇴근 기록',
    `${input.shift.date} ${input.workplaceName}`,
    `실제 근무 ${input.shift.actualStart}~${input.shift.actualEnd} · 휴게 ${input.breakMinutes === undefined ? '확인 필요' : `${input.breakMinutes}분`}`,
    workedMinutes === undefined ? '실제 휴게를 확인하면 휴게 제외 시간을 계산해요.' : `휴게 제외 ${Math.floor(workedMinutes / 60)}시간 ${workedMinutes % 60}분`,
    estimate === undefined ? '기본 임금 참고: 근무일 시급 또는 실제 휴게 확인 필요' : `기본 임금 참고 ${estimate.toLocaleString('ko-KR')}원 (${input.hourlyRate!.toLocaleString('ko-KR')}원 × 근무시간)`,
    '주휴·연장·야간·휴일 항목은 계약 조건과 월 전체 기록을 월화수당 리포트에서 따로 확인하세요.',
  ].join('\n');
}

export function firstJobConsultationQuestion(input: { ageBand: FirstJobPlan['ageBand']; workplaceName: string; shift?: LocalShift; breakMinutes?: number; missingLabels: string[] }): string {
  const shiftLine = input.shift?.actualStart && input.shift.actualEnd
    ? `최근 실제 근무는 ${input.shift.date} ${input.shift.actualStart}~${input.shift.actualEnd}, 휴게 ${input.breakMinutes === undefined ? '확인 전' : `${input.breakMinutes}분`}이었습니다.`
    : '아직 실제 퇴근 기록은 남기지 않았습니다.';
  const missing = input.missingLabels.length ? `아직 확인하지 못한 항목은 ${input.missingLabels.join(', ')}입니다.` : '계약 전 확인 항목은 모두 확인했습니다.';
  return [
    '안녕하세요. 첫 아르바이트 근로조건을 확인하고 싶습니다.',
    input.ageBand === 'unknown' ? `${input.workplaceName}에서 일할 예정이거나 일하고 있으며, 상담 전에 제 나이 구간을 확인하겠습니다.` : `저는 ${input.ageBand === 'under18' ? '만 18세 미만' : '만 18세 이상'}이고, ${input.workplaceName}에서 일할 예정이거나 일하고 있습니다.`,
    shiftLine,
    missing,
    '제 나이와 계약·실제 근무 조건에서 근로계약서, 보호자 서류, 근로시간, 주휴·야간·연장수당 중 무엇을 어떻게 확인하면 되는지 안내 부탁드립니다.',
  ].join('\n');
}
